/**
 * Content moderation verdicts for posts, comments and chat messages.
 *
 * The API serialises this enum as a **string** — `WLB.Services.API/Program.cs`
 * registers a global `JsonStringEnumConverter`, so `ContentModerationStatus?`
 * reaches the client as `"HeldForReview"`, never as the byte `3`. Comparing
 * against numbers (as an earlier handoff note suggested) silently never
 * matches, so every read goes through `readModerationStatus` first.
 */

import { createModerationEnumReader } from './moderationEnum';

export const MODERATION_STATUS = {
  /** An admin allowed it. Visible to everyone, and final. */
  approved: 'Approved',
  /**
   * Screening never ran — disabled, misconfigured, timed out or deferred.
   * Visible to everyone (the fail-open default). On freshly created content
   * this means screening failed, so it is a diagnostic signal, not a verdict.
   */
  notScreened: 'NotScreened',
  /** Flagged, awaiting admin review. Visible to the author and admins only. */
  heldForReview: 'HeldForReview',
  /** Confirmed violation. Visible to admins only, and final. */
  rejected: 'Rejected',
  /** Screening completed and found the content clean. Visible to everyone. */
  passed: 'Passed',
} as const;

export type ModerationStatus =
  (typeof MODERATION_STATUS)[keyof typeof MODERATION_STATUS];

/**
 * Accepts the string name the read APIs send and the 1-based ordinal the
 * database and the admin tables use (`Approved` = 1 … `Passed` = 5).
 *
 * Those numbers are identifiers, not a severity scale: `Passed` is the highest
 * value yet the most benign verdict, so never compare them with `<` or `>`.
 */
const normalize = createModerationEnumReader(Object.values(MODERATION_STATUS));

/** The verdicts every viewer can see. Membership test, never a comparison. */
const PUBLICLY_VISIBLE: readonly ModerationStatus[] = [
  MODERATION_STATUS.approved,
  MODERATION_STATUS.notScreened,
  MODERATION_STATUS.passed,
];

/** The raw shapes a `moderationStatus` can arrive in before normalisation. */
export type RawModerationStatus = string | number | null | undefined;

/**
 * Reads `moderationStatus` off an API payload, tolerating the PascalCase
 * spelling some stored procedures project, and normalises it to the canonical
 * string form. Returns `null` when moderation did not run for the content.
 *
 * Accepts either the container (`readModerationStatus(dto)`) or the bare value
 * (`readModerationStatus(dto.moderationStatus)`).
 */
export const readModerationStatus = (
  source: unknown,
): ModerationStatus | null => {
  if (source == null) {
    return null;
  }
  if (typeof source === 'string' || typeof source === 'number') {
    return normalize(source);
  }
  if (typeof source !== 'object') {
    return null;
  }
  const record = source as Record<string, RawModerationStatus>;
  return normalize(record.moderationStatus ?? record.ModerationStatus);
};

/**
 * True only for `HeldForReview`. This is the single status the content UI acts
 * on: the author sees their own held content with an "under review" marker,
 * everyone else never receives the row at all.
 *
 * An author editing held content re-runs screening, so a clean edit answers
 * `Passed` and the marker must be driven off the fresh value rather than the
 * cached one.
 */
export const isHeldForReview = (status: unknown): boolean =>
  readModerationStatus(status) === MODERATION_STATUS.heldForReview;

/**
 * True when every viewer can see the content: `Approved`, `NotScreened`,
 * `Passed`, or no verdict at all (surfaces that carry no moderation field).
 *
 * Use this instead of comparing the numeric form — the values are identifiers,
 * so `status <= 2` silently excludes the now-common `Passed`.
 */
export const isPubliclyVisible = (status: unknown): boolean => {
  const normalized = readModerationStatus(status);
  return normalized === null || PUBLICLY_VISIBLE.includes(normalized);
};

/**
 * True for `NotScreened`, meaning screening did not run. The content published
 * either way, so this is for internal diagnostics only and must never surface
 * to members.
 */
export const isScreeningSkipped = (status: unknown): boolean =>
  readModerationStatus(status) === MODERATION_STATUS.notScreened;
